import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { FaStar, FaVideo } from 'react-icons/fa';

import { Song } from 'interfaces';

import { AppLocale, BAND_NAME, BAND_NAME_JP } from 'constant';

import { LanguageContext } from 'context/languageContext';

import {
  SongsListWrapper,
  SongItem,
  SongNumber,
  SongName,
  SongAlbumName,
  SongNameLink,
  SongTitle,
  SongSide,
  SongSideBox,
  WiderSongSideBox,
  StyledInlineLink
} from './SongsList.styles';
import { getAlbum, getSong } from 'utils';

export interface SongsListProps {
  songs: Song[];
  className?: string;
  listType?: 'album' | 'all' | 'singles' | 'concert';
  internationalAlbum?: boolean;
}

const SongsList = ({ songs, className = '', listType = 'album', internationalAlbum = false }: SongsListProps) => {
  const intl = useIntl();
  const { locale } = useContext(LanguageContext);

  const variables = {
    bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
    albumKanjoEffect: getAlbum('kanjo-effect', false, locale, 'name'),
    songJust: <StyledInlineLink to={getSong('just', locale, 'link')}>{getSong('just', locale, 'name')}</StyledInlineLink>
  };

  return songs ? (
    <SongsListWrapper className={className}>
      {
        songs.map((song, index) =>  {
          const songName = internationalAlbum ? song.international?.name || song.name : song.name;
          const songEnglishName = internationalAlbum ? song.international?.englishName || song.englishName : song.englishName;

          return typeof song !== 'string' ? (
            <SongItem key={song.id}>
              <SongName>
                <SongNumber>{listType === 'album' ? song.albumSongOrder : song.isEncore ? index : index + 1 }.</SongNumber>
                <SongNameLink to={`/albums/${song.albumId}/${song.id}`}>
                  {
                    listType === 'all' ||  listType === 'concert'
                      ? (
                        <SongAlbumName>
                          {locale === AppLocale.EN ? song?.albumEnglishName || song?.albumName : song?.albumName}
                        </SongAlbumName>
                      )
                      : null
                  }
                  <SongTitle>
                    {
                      listType === 'singles'
                        ? locale === AppLocale.EN ? songEnglishName || songName : songName
                        : locale === AppLocale.EN ? song.englishName || song.name : song.name
                    }
                  </SongTitle>
                </SongNameLink>
              </SongName>
              <SongSide>
                {
                  listType === 'singles' || listType === 'concert'
                    ? (
                      <WiderSongSideBox color="primary">
                        {intl.formatMessage({ id: 'albums.releaseDate' })} {intl.formatDate(new Date(song.releaseDate), {
                          year: 'numeric', month: 'short', day: 'numeric'
                        })}
                    </WiderSongSideBox>
                    )
                    : (
                  <React.Fragment>
                      {
                        song.musicVideo ? (
                          <SongSideBox color="primary">
                            <Link to={song.musicVideo} target="_blank">
                              <FaVideo title={intl.formatMessage({ id: 'albums.details.musicVideo' })} />
                            </Link>
                          </SongSideBox>
                        ) : null
                      }
                    <SongSideBox color="secondary">
                      {
                        song.single ? (
                          <FaStar title={intl.formatMessage({ id: 'albums.details.single.title' })} />
                        ) : null
                      }
                    </SongSideBox>
                    <SongSideBox>
                      {song.duration}
                    </SongSideBox>
                  </React.Fragment>
                  )
                }
              </SongSide>
            </SongItem>
          ) : (
            <SongItem key={index} itemType={song}>
              <SongName>
                <SongNumber>{song !== 'encore' && song !== 'encore2' ? `${index + 1}.` : ''}</SongNumber>
                {
                  song !== 'opening' && song !== 'intro' && song !== 'instrumental' && song !== 'encore' && song !== 'encore2' ? (
                    <React.Fragment>
                      <SongAlbumName>{intl.formatMessage({ id: `setList.artist.${song}` }, { ...variables } )}</SongAlbumName>
                      <span>{intl.formatMessage({ id: `setList.song.${song}` }, { ...variables } )}</span>
                    </React.Fragment>
                  ) : <span>{intl.formatMessage({ id: `setList.${song}` }, { ...variables } )}</span>
                }
              </SongName>
            </SongItem>
          )
        })
      }
    </SongsListWrapper>
  ) : null
};

export default SongsList;
